import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import {
  OVERRIDE_AMOUNT_MAX,
  REASON_MAX,
} from '../../../../../Consts/subscription-control.const';
import {
  OverrideDialogData,
  OverrideDialogResult,
} from '../../../Interfaces/subscription-dialog.interface';

@Component({
  selector: 'app-override-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatIconModule, TranslateModule],
  templateUrl: './override-dialog.component.html',
  styleUrl: './override-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverrideDialogComponent {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _dialogRef =
    inject<MatDialogRef<OverrideDialogComponent, OverrideDialogResult>>(MatDialogRef);

  readonly data = inject<OverrideDialogData>(MAT_DIALOG_DATA);

  readonly reasonMax = REASON_MAX;
  readonly amountMax = OVERRIDE_AMOUNT_MAX;

  readonly amount = signal<number>(0);

  readonly isDiscount = computed<boolean>(
    () => this.amount() > 0 && this.amount() < this.data.currentUnitAmount,
  );

  readonly formGroup: FormGroup = this._formBuilder.group({
    override_unit_amount: [
      null,
      [Validators.required, Validators.min(0), Validators.max(OVERRIDE_AMOUNT_MAX)],
    ],
    reason: ['', [Validators.required, Validators.maxLength(REASON_MAX)]],
    ends_at: [''],
  });

  constructor() {
    this._registerAmountSync();
  }

  confirm(): void {
    if (!this.formGroup.valid || !this.isDiscount() || this.data.hasActiveOverride) {
      return;
    }

    this._dialogRef.close({
      amount: Number(this.formGroup.value.override_unit_amount),
      reason: String(this.formGroup.value.reason).trim(),
      endsAt: String(this.formGroup.value.ends_at ?? '') || null,
    });
  }

  dismiss(): void {
    this._dialogRef.close();
  }

  private _registerAmountSync(): void {
    this.formGroup.controls['override_unit_amount'].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this._applyAmount(Number(value)));
  }

  private _applyAmount(value: number): void {
    this.amount.set(Number.isFinite(value) ? value : 0);
  }
}
